import type { CapaActionListResponse } from '@/api/capa/types';
import type {
  AssessmentAttachmentResponse,
  MySlcpAssessmentsResponse,
  SlcpAssessmentDetail,
  SlcpAssessmentsParams,
  SlcpAssessmentsResponse,
  SlcpDataPointDef,
} from '@/api/slcp/types';
import {
  API_ROOT_URL,
  AUDITS_SERVICE_URL,
  BACKEND_URL,
  CAPA_SERVICE_URL,
  QUESTIONNAIRE_SERVICE_URL,
} from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  SlcpFindingsParams,
  SlcpFindingsResponse,
  SlcpSuggestedCapasResponse,
} from './types';

export async function fetchMySlcpAssessments(
  qs: string,
): Promise<MySlcpAssessmentsResponse> {
  return apiFetch<MySlcpAssessmentsResponse>(
    `/slcp/assessments?${qs}`,
    {},
    BACKEND_URL,
  );
}

export async function archiveSlcpAssessments(
  assessmentIds: string[],
): Promise<void> {
  return apiFetch<void>(
    '/slcp/assessments/archive',
    { method: 'POST', body: JSON.stringify({ assessmentIds }) },
    BACKEND_URL,
  );
}

export async function unarchiveSlcpAssessments(
  assessmentIds: string[],
): Promise<void> {
  return apiFetch<void>(
    '/slcp/assessments/archive',
    { method: 'DELETE', body: JSON.stringify({ assessmentIds }) },
    BACKEND_URL,
  );
}

export async function triggerGatewayAssessmentFetch(): Promise<void> {
  return apiFetch<void>(
    '/v1/slcp-assessments/fetch-assessments',
    { method: 'POST' },
    AUDITS_SERVICE_URL,
  );
}

export async function fetchAssessmentAttachmentLink(
  assessmentId: string,
): Promise<AssessmentAttachmentResponse> {
  return apiFetch<AssessmentAttachmentResponse>(
    `/v1/slcp-assessments/${assessmentId}/attachment`,
    {},
    AUDITS_SERVICE_URL,
  );
}

export async function fetchAssessmentById(
  assessmentId: string,
): Promise<SlcpAssessmentDetail> {
  return apiFetch<SlcpAssessmentDetail>(
    `/v1/slcp-assessments/${assessmentId}`,
    {},
    AUDITS_SERVICE_URL,
  );
}

export async function fetchProfileDataPoints(): Promise<SlcpDataPointDef[]> {
  return apiFetch<SlcpDataPointDef[]>(
    '/v1/data-points',
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function fetchSlcpSuggestedCapas(
  assessmentId: string,
  body: { page: number; limit: number; filters: Record<string, unknown> },
): Promise<SlcpSuggestedCapasResponse> {
  return apiFetch<SlcpSuggestedCapasResponse>(
    `/slcp/assessments/${assessmentId}/suggested-capas`,
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export interface SlcpQuestionFinding {
  id: string;
  title?: string | null;
  description?: string | null;
  impactArea?: string | null;
  impactAreaId?: string | null;
}

export async function fetchSlcpQuestionFindings(
  assessmentId: string,
  qs: string,
): Promise<{ data?: SlcpQuestionFinding[] }> {
  return apiFetch<{ data?: SlcpQuestionFinding[] }>(
    `/v1/findings/audit/${assessmentId}/list?${qs}`,
    {},
    CAPA_SERVICE_URL,
  );
}

export async function fetchSlcpActions(
  qs: string,
): Promise<CapaActionListResponse> {
  return apiFetch<CapaActionListResponse>(
    `/v1/actions/?${qs}`,
    {},
    CAPA_SERVICE_URL,
  );
}

export async function createSlcpAssessmentHistory(body: {
  assessmentId: string;
  syncedAnswers?: unknown[];
}): Promise<void> {
  return apiFetch<void>(
    '/v1/slcp-assessments/history',
    { method: 'POST', body: JSON.stringify(body) },
    AUDITS_SERVICE_URL,
  );
}

/**
 * Cross-feature SLCP endpoints. Used by both the SLCP feature and the
 * network company-profile SLCP tab via the shared SlcpTable component.
 */

export async function fetchSlcpAssessments(
  params: SlcpAssessmentsParams,
): Promise<SlcpAssessmentsResponse> {
  const body = {
    page: params.page,
    limit: params.limit,
    ...(params.filter && Object.keys(params.filter).length > 0
      ? { filter: params.filter }
      : {}),
  };

  return apiFetch<SlcpAssessmentsResponse>(
    '/api/slcp/assessments/network-list',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

export async function requestAccessToAssessment(body: {
  assessment_id: string;
}): Promise<void> {
  return apiFetch<void>(
    '/api/slcp/assessments/requests',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

export async function requestMissingAccessToAssessment(body: {
  companyIds: string[];
}): Promise<void> {
  return apiFetch<void>(
    '/api/slcp/assessments/missing-access-request',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * SLCP assessment findings — corrective-action-plan-service legacy endpoint.
 * Returns audit findings linked to the assessment with resolved impactArea +
 * SLCP-specific metadata under `additionalInformation`.
 */
export async function fetchSlcpFindings(
  assessmentId: string,
  params: SlcpFindingsParams,
): Promise<SlcpFindingsResponse> {
  const qs = new URLSearchParams();
  qs.set('page', String(Math.max(0, params.page - 1)));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  const filter: Record<string, unknown> = {};
  if (params.isLegal !== undefined) filter.isLegal = params.isLegal;
  qs.set('filter', JSON.stringify(filter));
  return apiFetch<SlcpFindingsResponse>(
    `/v1/findings/audit/${assessmentId}/list?${qs.toString()}`,
    {},
    CAPA_SERVICE_URL,
  );
}
